import { addSeconds, isFuture } from 'date-fns';
import { FireState, IgnoreAutoFireMode } from 'enums/firing';
import { IFireStateSnapshot } from 'interfaces/i-firing';
import { QueueState } from 'lib_ts/enums/machine-msg.enum';
import { OnDemandStatus } from 'lib_ts/enums/on-demand.enums';

/** decides which fire state the machine button should render, evaluated on every heartbeat */
export const getFireState = (snapshot: IFireStateSnapshot): FireState => {
  const { fireStateDates: fsDates, ignoreTR, isTraining, onDemand } = snapshot;

  // MARK: non-active queue state
  switch (snapshot.queueState) {
    case QueueState.MachineDC: {
      return FireState.OfflineMachine;
    }

    case QueueState.UserDC: {
      return FireState.OfflineUser;
    }

    case QueueState.Busy: {
      return FireState.Busy;
    }

    case QueueState.Active:
    default: {
      break;
    }
  }

  // MARK: abnormal states
  if (snapshot.suspended) {
    // e.g. machine connects in the bg while pitch data dialog is open and sidebar firing context is suspended
    return FireState.Paused;
  }

  // loading
  if (isFuture(fsDates.loadingUntil)) {
    return FireState.Loading;
  }

  // sending (e.g. re-aiming between data collection shots), the user has nothing to do until it lands
  if (isFuture(snapshot.sendingUntil)) {
    return FireState.Loading;
  }

  // firing
  if (isFuture(fsDates.awaitFiringUntil)) {
    return FireState.Firing;
  }

  // no ball
  if (snapshot.ballCount === 0) {
    return FireState.DropBall;
  }

  // ball count
  if (snapshot.ballCount !== 1) {
    // e.g. -1 => lighting error, 2 => multiple balls
    return FireState.BallError;
  }

  // MARK: training

  if (
    isTraining && // training
    !snapshot.pitchTraining // no pitch selected
  ) {
    return FireState.LoadPitch;
  }

  if (
    isTraining && // training
    snapshot.requiresSend // requires send
  ) {
    // ! data collection re-aims after every shot, and the machine cannot take that send while it
    // ! is still firing; LoadPitch renders disabled without s2m so offering it just flashes
    return snapshot.s2mReady ? FireState.LoadPitch : FireState.Loading;
  }

  // has been fired in the last 30 seconds
  const firedRecently =
    fsDates.lastFireDate && isFuture(addSeconds(fsDates.lastFireDate, 30));

  // not ignoring training responses and waiting for training response
  const awaitingTR = !ignoreTR && isFuture(fsDates.awaitTrainingUntil);

  if (
    isTraining && // training
    firedRecently && // fired recently
    awaitingTR // awaiting training response
  ) {
    return FireState.AwaitingTR;
  }

  // MARK: not training

  if (
    !isTraining && // not training
    !snapshot.pitchBP // nothing selected
  ) {
    // * this may lag behind when switching between pitches, as pitchBP may not match the latest selected pitch
    return FireState.LoadPitch;
  }

  // where the ms comes from
  const viaMode =
    snapshot.pitchBP && snapshot.pitchBP.debugMs
      ? 'debugMs'
      : onDemand
        ? 'odms'
        : 'tms';

  switch (viaMode) {
    // MARK: via debugMs
    case 'debugMs': {
      if (
        !isTraining && // not training
        snapshot.requiresSend // requires send
      ) {
        return FireState.LoadPitch;
      }
      break;
    }

    // MARK: via ODMS
    case 'odms': {
      if (
        !isTraining && // not training
        snapshot.pitchBP // pitch is selected
      ) {
        const mxOdms = snapshot.pitchBP._mx_odms;

        switch (mxOdms?.status) {
          case OnDemandStatus.Ready:
          case OnDemandStatus.Warning: {
            if (snapshot.requiresSend) {
              return FireState.LoadPitch;
            }
            break;
          }

          case OnDemandStatus.None:
          case OnDemandStatus.NotReady:
          default: {
            // todo: this is probably where we need a new state for OD calibration shots
            return FireState.UncalibratedOD;
          }
        }
      }
      break;
    }

    // MARK: via TMS
    case 'tms': {
      // todo: investigate whether we can access the tms from store directly instead of via _mx_tms
      if (
        !isTraining && // not training
        snapshot.pitchBP && // pitch is selected
        !snapshot.pitchBP._mx_tms?._mx_trained // pitch is untrained
      ) {
        return FireState.UntrainedBP;
      }

      if (
        !isTraining && // not training
        snapshot.pitchBP && // pitch is selected
        snapshot.requiresSend // requires send
      ) {
        return FireState.LoadPitch;
      }
      break;
    }

    default: {
      // shouldn't trigger
      break;
    }
  }

  // MARK: common date checks

  // ? when lastR2FDate is now, it fails "isPast"--in this case, it should be treated as ready
  if (isFuture(fsDates.lastR2fDate)) {
    return FireState.NotReadyToFire;
  }

  if (isTraining && snapshot.trainingShots) {
    // check that we do not allow sending during training until aiming shots
    // (e.g. from previous pitch in training queue) are done updating
    const staleShots = snapshot.trainingShots.filter(
      // pitch id mismatch => either pitch and/or shots from props changed recently
      (s) => s.pitch_id !== snapshot.pitchTraining?._id
    );

    if (staleShots && staleShots.length > 0) {
      return FireState.NotReadyToFire;
    }
  }

  if (!snapshot.autoFire) {
    // auto-fire is disabled
    return FireState.ReadyToManualFire;
  }

  // auto-fire is enabled, are we ignoring for any reason?
  switch (snapshot.ignoreAf) {
    case IgnoreAutoFireMode.Disabled: {
      return FireState.ReadyToAutoFire;
    }

    case IgnoreAutoFireMode.Temporary: {
      return FireState.Loading;
    }

    case IgnoreAutoFireMode.Permanent:
    default: {
      return FireState.ReadyToManualFire;
    }
  }
};
